import StudioDocuments, {
  IStudioDocuments,
  IStudioDocumentsApiModel,
} from '@models/StudioDocuments.model';
import Address, { IAddress, IAddressApiModel } from './Address.model';
import StudioContact, {
  IStudioContact,
  IStudioContactApiModel,
} from './StudioContact.model';
import StudioResources, {
  IStudioResources,
  IStudioResourcesApiModel,
} from './StudioResources.model';

const ONE_DAY_TIME = 1000 * 60 * 60 * 24;

// eslint-disable-next-line no-shadow
export enum StudioType {
  PAID = 'paid',
  TRIAL = 'trial',
  PILOT = 'pilot',
}

export enum StudioStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

export const getStudioRemainDays = (
  studioType: StudioType,
  firstLoginAt?: Date,
  paidExpirationDate?: Date,
  trialExpirationDays?: number,
): number | undefined => {
  if (studioType === StudioType.PILOT) return undefined;
  if (studioType === StudioType.PAID && paidExpirationDate) {
    const todayDate = new Date();
    const differenceInMilisec =
      todayDate.getTime() - paidExpirationDate.getTime();
    const differenceInDays = Math.ceil(differenceInMilisec / ONE_DAY_TIME);
    return differenceInDays > 0 ? differenceInDays : 0;
  }
  if (studioType === StudioType.TRIAL && trialExpirationDays && firstLoginAt) {
    const todayDate = new Date();
    const differenceInMilisec =
      todayDate.getTime() -
      firstLoginAt.getTime() +
      trialExpirationDays * ONE_DAY_TIME;
    const differenceInDays = Math.ceil(differenceInMilisec / ONE_DAY_TIME);
    return differenceInDays > 0 ? differenceInDays : 0;
  }
  return undefined;
};

export interface IStudioAuth0 {
  id: string;
  name: string;
  display_name: string;
  branding: {
    logo_url: string;
  };
}
export interface IStudio {
  id: string;
  name: string;
  auth0Id: string;
  createdAt: Date;
  updatedAt: Date;
  auth0?: IStudioAuth0;
  address?: IAddress;
  equipmentCount: number;
  equipmentCountWithTemProduct: number;
  usersCount: number;
  lastLoginAtDate?: Date;
  firstLoginAt?: Date;
  paidExpirationDate?: Date;
  trialExpirationDays?: number;
  type: StudioType;
  status: StudioStatus;
  documentStudios: IStudioDocuments[];
  studioContacts: IStudioContact[];
  resourceStudios: IStudioResources[];
  // fields
  equipmentMatched: number;
  remainDays?: number;
  expirationDay?: Date;
  durationDays?: number;
}

export interface IStudioApiModel {
  id: string;
  name: string;
  auth0Id: string;
  createdAt: string;
  updatedAt: string;
  resourceStudios: IStudioResourcesApiModel[];
  studioContacts: IStudioContactApiModel[];
  documentStudios: IStudioDocumentsApiModel[];
  type: StudioType;
  status: StudioStatus;
  auth0?: IStudioAuth0;
  address?: IAddressApiModel;
  expirationDay: string;
  durationDays?: number;
}

export default class Studio implements IStudio {
  public readonly id: string;

  public readonly name: string;

  public readonly auth0Id: string;

  public readonly address?: IAddress;

  public readonly auth0?: IStudioAuth0;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly equipmentCount: number;

  public readonly equipmentCountWithTemProduct: number;

  public readonly usersCount: number;

  public readonly equipmentMatched: number;

  public readonly lastLoginAtDate?: Date;

  public readonly firstLoginAt?: Date;

  public readonly paidExpirationDate?: Date;

  public readonly trialExpirationDays?: number;

  public readonly type: StudioType;

  public readonly status: StudioStatus;

  public readonly remainDays?: number;

  public readonly expirationDay?: Date;

  public readonly durationDays?: number;

  public readonly resourceStudios: IStudioResources[];

  public readonly documentStudios: IStudioDocuments[];

  public readonly studioContacts: IStudioContact[];

  constructor(
    id: string,
    name: string,
    auth0Id: string,
    createdAt: Date,
    updatedAt: Date,
    equipmentCount: number,
    equipmentCountWithTemProduct: number,
    usersCount: number,
    equipmentMatched: number,
    type: StudioType,
    status: StudioStatus,
    resourceStudios: IStudioResources[],
    documentStudios: IStudioDocuments[],
    studioContacts: IStudioContact[],
    expirationDay?: Date,
    durationDays?: number,
    address?: IAddress,
    auth0?: IStudioAuth0,
    lastLoginAtDate?: Date,
    firstLoginAt?: Date,
    paidExpirationDate?: Date,
    trialExpirationDays?: number,
    remainDays?: number,
  ) {
    this.id = id;
    this.name = name;
    this.auth0Id = auth0Id;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.address = address;
    this.auth0 = auth0;
    this.equipmentCount = equipmentCount;
    this.equipmentCountWithTemProduct = equipmentCountWithTemProduct;
    this.usersCount = usersCount;
    this.equipmentMatched = equipmentMatched;
    this.type = type;
    this.status = status;
    this.lastLoginAtDate = lastLoginAtDate;
    this.firstLoginAt = firstLoginAt;
    this.paidExpirationDate = paidExpirationDate;
    this.trialExpirationDays = trialExpirationDays;
    this.remainDays = remainDays;
    this.resourceStudios = resourceStudios;
    this.documentStudios = documentStudios;
    this.studioContacts = studioContacts;
    this.expirationDay = expirationDay;
    this.durationDays = durationDays;
  }

  static fromApiModel(apiModel: IStudioApiModel) {
    const { id, name, auth0Id, auth0, type, status, durationDays } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const expirationDay = new Date(Date.parse(apiModel.expirationDay));
    const address = apiModel.address
      ? Address.fromApiModel(apiModel.address)
      : undefined;

    const resourceStudios = apiModel.resourceStudios
      ? StudioResources.arrayFromApiModel(apiModel.resourceStudios)
      : [];
    const documentStudios = apiModel.documentStudios
      ? StudioDocuments.arrayFromApiModel(apiModel.documentStudios)
      : [];
    const studioContacts = apiModel.studioContacts
      ? StudioContact.arrayFromApiModel(apiModel.studioContacts)
      : [];
    return new Studio(
      id,
      name,
      auth0Id,
      createdAt,
      updatedAt,
      0,
      0,
      0,
      0,
      type,
      status,
      resourceStudios,
      documentStudios,
      studioContacts,
      expirationDay,
      durationDays,
      address,
      auth0,
    );
  }

  static arrayFromApiModel(arrayApiModel: IStudioApiModel[]) {
    const array = arrayApiModel.map(apiModel => Studio.fromApiModel(apiModel));

    return array;
  }
}
